import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { useI18n } from '../../i18n/I18nContext';
import { mockUser } from '../../data/mockData';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const RATING_OPTIONS = [
  { value: 1, emoji: '😟', label: 'Poor' },
  { value: 2, emoji: '😐', label: 'Fair' },
  { value: 3, emoji: '🙂', label: 'Good' },
  { value: 4, emoji: '😊', label: 'Great' },
  { value: 5, emoji: '🤩', label: 'Excellent' },
];

const CATEGORY_OPTIONS = [
  'Navigation & Layout',
  'Data & Reports',
  'Performance & Speed',
  'Visual Design',
  'Missing Feature',
  'Other',
];

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const { t } = useI18n();
  const [rating, setRating] = useState<number | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const toggleCategory = (cat: string) => {
    setCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const handleSubmit = () => {
    // Build query params for Salesforce
    const params = new URLSearchParams();
    if (rating) params.set('rating', rating.toString());
    if (categories.length) params.set('categories', categories.join(','));
    if (message) params.set('message', message);
    params.set('pageUrl', window.location.href);
    params.set('agentCode', mockUser?.id || 'unknown');

    // Open Salesforce feedback page with data
    const sfUrl = `https://cloud.wfg.transamerica.com/FeedbackLog?${params.toString()}`;
    window.open(sfUrl, '_blank');

    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setRating(null);
      setCategories([]);
      setMessage('');
      onClose();
    }, 1500);
  };

  const canSubmit = rating !== null || message.trim().length > 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[2000] bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[2001] flex items-center justify-center p-4"
          >
            <div
              className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden"
              onClick={e => e.stopPropagation()}
            >

              {submitted ? (
                <div className="p-8 text-center">
                  <div className="text-4xl mb-3">✅</div>
                  <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100 mb-1">Thank you!</h3>
                  <p className="text-sm text-neutral-500">Your feedback helps us improve.</p>
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-700">
                    <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">Send Feedback</h2>
                    <button
                      onClick={onClose}
                      className="p-2 rounded-md text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                      aria-label="Close"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>

                  <div className="px-5 py-4 space-y-5">
                    {/* Rating */}
                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
                        How's your experience so far?
                      </label>
                      <div className="flex gap-2">
                        {RATING_OPTIONS.map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => setRating(opt.value)}
                            className={clsx(
                              'flex-1 flex flex-col items-center gap-1 py-2 rounded-lg border transition-all text-center',
                              rating === opt.value
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                : 'border-neutral-200 dark:border-neutral-600 hover:border-neutral-300 dark:hover:border-neutral-500'
                            )}
                          >
                            <span className="text-xl">{opt.emoji}</span>
                            <span className="text-[0.625rem] font-medium text-neutral-500 dark:text-neutral-400">{opt.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Categories */}
                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
                        What's this about? <span className="font-normal text-neutral-400">(optional)</span>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {CATEGORY_OPTIONS.map(cat => (
                          <button
                            key={cat}
                            onClick={() => toggleCategory(cat)}
                            className={clsx(
                              'px-3 py-1.5 rounded-full text-xs font-medium border transition-all',
                              categories.includes(cat)
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                                : 'border-neutral-200 dark:border-neutral-600 text-neutral-600 dark:text-neutral-400 hover:border-neutral-300'
                            )}
                          >
                            {cat}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
                        Tell us more <span className="font-normal text-neutral-400">(optional)</span>
                      </label>
                      <textarea
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        placeholder="What's working? What could be better?"
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-neutral-200 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 resize-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-700 flex justify-end gap-3">
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={!canSubmit}
                      className={clsx(
                        'px-4 py-2 text-sm font-semibold rounded-lg transition-all',
                        canSubmit
                          ? 'bg-primary-500 text-white hover:bg-primary-600'
                          : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-400 cursor-not-allowed'
                      )}
                    >
                      Send Feedback
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
